import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, FileText, Code, Linkedin, UserCircle } from 'lucide-react';
import { useData } from '../context/DataContext';
import ApiService from '../services/api';

const Authors = () => {
  const { trackPageView } = useData();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    trackPageView('authors');
  }, [trackPageView]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await ApiService.getAuthors();
        if (!cancelled) {
          setAuthors(res.authors || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load authors');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Our Authors</h1>
        <p className="page-description">
          Meet the SQL Server professionals who contribute articles, scripts, and tools on MSSQLDBATeam.com.
        </p>
      </div>

      {loading && <p>Loading authors…</p>}
      {error && <div className="auth-alert auth-alert-error">{error}</div>}

      {!loading && !error && authors.length === 0 && (
        <p>No approved authors yet. Be the first to <Link to="/register">apply as a contributor</Link>.</p>
      )}

      <div className="authors-grid">
        {authors.map((author) => (
          <Link key={author.id} to={`/authors/${author.id}`} className="author-card author-card-link">
            <div className="author-header">
              {author.avatar_url ? (
                <img src={author.avatar_url} alt={author.full_name} className="author-image" />
              ) : (
                <div className="author-image author-image-fallback">
                  <UserCircle size={36} />
                </div>
              )}
              <div className="author-info">
                <h3 className="author-name">{author.full_name}</h3>
                <p className="author-title">{author.job_title || (author.role === 'admin' ? 'Admin & Author' : 'Contributor')}</p>
                {author.professional_experience && (
                  <div className="author-experience">
                    <Award size={16} />
                    <span>{author.professional_experience.length > 80
                      ? `${author.professional_experience.slice(0, 80)}…`
                      : author.professional_experience}</span>
                  </div>
                )}
              </div>
            </div>

            {author.specialization && (
              <div className="author-specialization">
                <strong>Specialization:</strong> {author.specialization}
              </div>
            )}

            {author.bio && <p className="author-bio">{author.bio}</p>}

            {author.linkedin_url && (
              <div className="author-linkedin">
                <Linkedin size={16} />
                <span>LinkedIn profile</span>
              </div>
            )}

            <div className="author-contributions">
              <h4>Contributions</h4>
              <div className="contributions-grid">
                <div className="contribution-item">
                  <Code size={20} color="#0f766e" />
                  <span>{author.contributions?.scripts || 0} Scripts</span>
                </div>
                <div className="contribution-item">
                  <FileText size={20} color="#0e7490" />
                  <span>{author.contributions?.articles || 0} Articles</span>
                </div>
              </div>
            </div>
            <span className="author-view-link">View author page →</span>
          </Link>
        ))}
      </div>

      <div className="join-authors">
        <h2>Want to Contribute?</h2>
        <p>
          Experienced SQL Server professionals can apply as contributors. After admin approval,
          you can publish articles exclusive to MSSQLDBATeam.com.
        </p>
        <div className="contact-info">
          <Link to="/register" className="btn btn-primary">Apply to contribute</Link>
          <p style={{ marginTop: '12px' }}>
            Or contact <a href="mailto:contact@mssqldbateam.com">contact@mssqldbateam.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Authors;
